import { Component, EventEmitter, Input, Output, inject } from "@angular/core";
import { FormBuilder, FormGroup, ReactiveFormsModule } from "@angular/forms";
import { UiKitModule } from "@app/ui-kit.module";
import { PictureCardComponent } from "@components/picture-card/picture-card.component";
import { RefreshProductsHintComponent } from "@components/refresh-products-hint/refresh-products-hint.component";
import { Gas, GasProduct, LocalizeGasNamePipe } from "@domain/gas/gas";
import { FgdsProductType, Product } from "@domain/product/product";
import { ProductImage } from "@domain/product/product-image";
import { ProductService } from "@domain/product/product.service";
import { FlameDetectorType } from "@project/flame-detector-configuration/flame-detector-type";
import { first } from "rxjs";

@Component({
  selector: "app-flame-detector-family-selector",
  standalone: true,
  templateUrl: "./flame-detector-family-selector.component.html",
  styleUrls: ["./flame-detector-family-selector.component.scss"],
  imports: [UiKitModule, ReactiveFormsModule, LocalizeGasNamePipe, PictureCardComponent, RefreshProductsHintComponent],
  providers: [],
})
export class FlameDetectorFamilySelectorComponent {
  private formBuilder = inject(FormBuilder);
  private productService = inject(ProductService);

  @Input() set selectedSubstance(gas: Gas | undefined) {
    this.clearFamilySelection();
    if (gas) {
      this.selectedGas = gas;
      this.collectProducts();
    }
  }

  @Input() set originalFlameDetector(family: FlameDetectorType | undefined) {
    if (family) {
      this.selectedFamilyForm?.patchValue({ selectedType: family });
    }
  }

  @Output() familySelect$ = new EventEmitter<FlameDetectorType>();

  protected selectedGas?: Gas;
  protected selectedType?: FlameDetectorType;
  protected selectableFamilyTypes: FlameDetectorType[] = [];

  private selectableGasProducts: GasProduct[] = [];

  constructor() {
    this.selectedFamilyForm = this.formBuilder.group({
      selectedType: [null],
    });

    this.selectedFamilyForm.valueChanges.subscribe(() => {
      this.selectedType = this.selectedFamilyForm.value.selectedType;
      this.familySelect$.emit(this.selectedType);
    });
  }

  protected select(type: FlameDetectorType) {
    this.selectedFamilyForm.patchValue({ selectedType: type });
  }

  private collectProducts() {
    if (!this.selectedGas) {
      return;
    }

    this.selectableGasProducts = this.selectedGas.products ?? [];
    const productIds = this.selectableGasProducts.map((gasProduct) => gasProduct.productId);
    if (!productIds.length) return;

    this.productService
      .getProductsByIds(productIds, FgdsProductType.FLAME_DETECTOR)
      .pipe(first())
      .subscribe((products) => this.setSelectableFamilies(products));
  }

  private setSelectableFamilies(products: Product[]) {
    const productsById = new Map(products.map((product) => [product.id, product]));
    const availableFamilies = new Map<string, FlameDetectorType>();

    for (const gasProduct of this.selectableGasProducts) {
      const product = productsById.get(gasProduct.productId);
      if (!product?.isAvailable) continue;
      // Products are already restricted to FgdsProductType.FLAME_DETECTOR, so every parent family is a
      // flame-detector family. `rawName` is only a legacy fallback for gas data cached before parentFamilies existed.
      const familyNames = product.parentFamilies.length ? product.parentFamilies : [gasProduct.rawName];

      for (const familyName of familyNames) {
        if (!familyName) continue;
        const trimmedFamilyName = familyName.trim();
        const normalizedFamilyName = trimmedFamilyName.toLowerCase();
        if (!normalizedFamilyName || availableFamilies.has(normalizedFamilyName)) continue;

        availableFamilies.set(normalizedFamilyName, FlameDetectorType.fromFamilyName(trimmedFamilyName, product.cantoImageId));
      }
    }

    this.selectableFamilyTypes = [...availableFamilies.values()].sort((first, second) => first.name.localeCompare(second.name));

    const selectedType = this.selectedFamilyForm.value.selectedType as FlameDetectorType | undefined;
    const matchingType = this.selectableFamilyTypes.find((type) => type.name === selectedType?.name);
    if (matchingType && matchingType !== selectedType) {
      this.selectedFamilyForm.patchValue({ selectedType: matchingType });
    }
  }

  private clearFamilySelection() {
    this.selectedType = undefined;
    this.selectableFamilyTypes = [];
    this.selectedGas = undefined;
    this.selectedFamilyForm?.patchValue({ selectedType: null }, { emitEvent: false });
  }

  protected selectedFamilyForm: FormGroup;
  protected readonly ProductImage = ProductImage;
}
